<template>
  <div class="dc-code-panel">
    <div class="box-title">
      <h4>
        <i class="fal fa-barcode-alt"></i>
        ລະຫັດ DC ຂອງສາຂາ (DC Code)
      </h4>
    </div>
    <p class="dc-code-hint">
      ລະຫັດນີ້ສະແດງໃນບິນ ແລະ ການຕິດຕາມພັດສະດຸ ເພື່ອບອກພະນັກງານວ່າຕ້ອງຍິງພັດສະດຸອອກໄປຫາ prefix ໃດ.
      ຄ່າເດີມແມ່ນ prefix ຂອງແຂວງທີ່ສາຂາຕັ້ງຢູ່ — ຖ້າສາຂານີ້ຕ້ອງໃຊ້ prefix ອື່ນ ສາມາດ override ໄດ້ຢູ່ບ່ອນນີ້.
    </p>

    <div v-if="loadError" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-exclamation-triangle" :style="{ marginRight: '6px' }"></i>
      ບໍ່ສາມາດໂຫຼດຂໍ້ມູນ DC Code ໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ ຫຼື ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ພາຍຫຼັງ.
      <vs-button transparent :style="{ display: 'inline-block', marginLeft: '8px' }" @click="retryLoad()">
        ລອງໃໝ່
      </vs-button>
    </div>

    <!--
      ບັນທຶກລົງຖານຂໍ້ມູນແລ້ວ ແຕ່ລ້າງ cache ບໍ່ໄດ້ — ບິນອາດຍັງພິມ prefix ເກົ່າ
      ຈົນກວ່າ cache ຈະໝົດອາຍຸ.
    -->
    <CacheStaleNotice :visible="cacheStale">ບິນອາດຍັງພິມ DC Code ເກົ່າ</CacheStaleNotice>

    <div class="columns is-multiline" :style="{ marginTop: '10px' }">
      <div class="column is-4">
        <div class="dc-code-card">
          <label class="label">ຄ່າເດີມ (ຕາມແຂວງຂອງສາຂາ)</label>
          <p class="dc-code-value">{{ info.default_prefix || '-' }}</p>
        </div>
      </div>
      <div class="column is-4">
        <div class="dc-code-card" :class="{ 'is-overridden': hasActiveOverride }">
          <label class="label">ຄ່າທີ່ສະແດງໃນບິນປັດຈຸບັນ</label>
          <p class="dc-code-value">{{ info.effective_prefix || '-' }}</p>
          <p v-if="hasActiveOverride" class="dc-code-note">
            Override: {{ info.override.old_prefix || '-' }}
            <i class="fal fa-arrow-right"></i>
            {{ info.override.new_prefix }} ({{ info.override.province_name }})
          </p>
          <p v-else-if="hasInactiveOverride" class="dc-code-note is-muted">
            ເຄີຍ override ເປັນ {{ info.override.new_prefix }} (ຍົກເລີກແລ້ວ)
          </p>
        </div>
      </div>
    </div>

    <div class="columns is-multiline">
      <div class="column is-4">
        <div class="field">
          <div class="control">
            <label class="label">
              ເລືອກ prefix ແຂວງໃໝ່ ເພື່ອ override
              <span class="text-danger">{{ server_errors.provinceId }}</span>
            </label>
            <model-list-select
                :list="prefixOptionList"
                v-model="provinceId"
                name="provinceId"
                placeholder="ກະລຸນາເລືອກ prefix..."
                option-text="label"
                option-value="id">
            </model-list-select>
          </div>
        </div>
      </div>
      <div class="column is-6 dc-code-actions">
        <vs-button :loading="btnLoading" :disabled="!provinceId || loadError" @click="SaveOverride()">
          <i class="fal fa-check-circle" :style="{ marginRight: '6px' }"></i>
          ບັນທຶກ Override
        </vs-button>
        <vs-button v-if="hasActiveOverride" danger flat :loading="removeLoading" @click="removeModalActive = true">
          <i class="fad fa-trash-alt" :style="{ marginRight: '6px' }"></i>
          ຍົກເລີກ Override (ກັບໄປໃຊ້ຄ່າເດີມ)
        </vs-button>
      </div>
    </div>

    <Loading v-if="isLoading"/>

    <vs-dialog prevent-close v-model="removeModalActive">
      <div class="con-content" :style="{ marginTop: '15px' }">
        <p :style="{ fontSize: '18px', color: '#195BFF' }">
          ກະລຸນາກົດຢືນຢັນ ເພື່ອຍົກເລີກ override ແລະ ກັບໄປໃຊ້ prefix ເດີມຂອງສາຂາ
        </p>
      </div>
      <div class="con-footer" :style="{ marginTop: '25px' }">
        <vs-button
            :loading="removeLoading"
            transparent :style="{ color: 'crimson' }"
            @click="RemoveOverride()"
        >
          ຢືນຢັນ
        </vs-button>
        <vs-button @click="removeModalActive = false" dark transparent>
          ຍົກເລີກ
        </vs-button>
      </div>
    </vs-dialog>
  </div>
</template>
<script>
import { ModelListSelect } from 'vue-search-select';
import Loading from '@/views/Loading';
import CacheStaleNotice from '@/components/CacheStaleNotice';

export default {
  components: { ModelListSelect, Loading, CacheStaleNotice },

  data: () => ({
    isLoading: false,
    loadError: false,
    /// The save committed but the backend could not invalidate its cached override map, so
    /// bills can keep printing the previous prefix until it expires.
    cacheStale: false,
    btnLoading: false,
    removeLoading: false,
    removeModalActive: false,
    provinceId: '',
    prefixOptions: [],
    info: {
      default_prefix: '',
      effective_prefix: '',
      override: null,
    },
    server_errors: {
      provinceId: '',
    },
  }),

  computed: {
    branchId() {
      return this.$route.params.id;
    },
    hasActiveOverride() {
      return !!(this.info.override && this.info.override.is_active);
    },
    hasInactiveOverride() {
      return !!(this.info.override && !this.info.override.is_active);
    },
    prefixOptionList() {
      return this.prefixOptions.map(item => ({
        id: item.id,
        label: `${item.prefix} - ${item.name}`,
      }));
    },
  },

  created() {
    this.fetchData();
    this.fetchOptions();
  },

  methods: {
    retryLoad() {
      this.loadError = false;
      this.fetchData();
      this.fetchOptions();
    },
    fetchData() {
      this.isLoading = true;
      /// A fresh load of the current server state is where a warning about a PAST save stops
      /// being useful — by then the cache has usually expired, and the card below shows the
      /// prefix actually in force.
      this.cacheStale = false;
      this.$axios.get('get-branch-dc-code-prefix/' + this.branchId).then(res => {
        this.info = res.data;
        this.isLoading = false;
      }).catch(() => {
        // Backend not deployed yet / outage: say so instead of rendering
        // empty values as if they were real data.
        this.isLoading = false;
        this.loadError = true;
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      });
    },
    fetchOptions() {
      this.$axios.get('list-branch-dc-code-prefix-options').then(res => {
        this.prefixOptions = res.data.prefixOptions || [];
      }).catch(() => {
        this.loadError = true;
      });
    },
    SaveOverride() {
      if (!this.provinceId) {
        return;
      }
      this.server_errors.provinceId = '';
      this.btnLoading = true;
      this.$axios.post('set-branch-dc-code-prefix/' + this.branchId, {
        provinceId: this.provinceId,
      }).then(res => {
        this.btnLoading = false;
        if (res.data.success == true) {
          this.info = res.data;
          this.provinceId = '';
          this.cacheStale = res.data.cache_stale === true;

          /// Saved either way — but when the cache could not be dropped, the operator has to
          /// know the printed bill may lag, or they will read an unchanged bill as a failed save.
          if (this.cacheStale) {
            this.$notification.openNotification(
              { title: 'DC Code', text: 'ບັນທຶກແລ້ວ ແຕ່ຍັງບໍ່ທັນມີຜົນທຸກບ່ອນ (~5 ນາທີ)' },
              'top-right', 'warn', 7000
            );
          } else {
            this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
          }
        }
      }).catch(error => {
        this.btnLoading = false;
        if (error.response && error.response.status === 422) {
          this.server_errors.provinceId = this.serverErrorMessage(error.response.data?.error);
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    RemoveOverride() {
      this.removeLoading = true;
      this.$axios.delete('delete-branch-dc-code-prefix/' + this.branchId).then(res => {
        this.removeLoading = false;
        this.removeModalActive = false;
        if (res.data.success == true) {
          this.info = res.data;
          this.provinceId = '';
          this.cacheStale = res.data.cache_stale === true;

          if (this.cacheStale) {
            this.$notification.openNotification(
              { title: 'DC Code', text: 'ຍົກເລີກແລ້ວ ແຕ່ຍັງບໍ່ທັນມີຜົນທຸກບ່ອນ (~5 ນາທີ)' },
              'top-right', 'warn', 7000
            );
          } else {
            this.$notification.OpenNotification_DeleteItem_OnSuccess('top-right', 'danger', 3000);
          }
        } else {
          // e.g. NOT_FOUND (already removed elsewhere) — refresh instead of
          // pretending the delete just succeeded.
          this.$notification.openNotification(
            { title: 'DC Code', text: 'ບໍ່ມີ override ໃຫ້ຍົກເລີກແລ້ວ — ໂຫຼດຂໍ້ມູນຄືນໃໝ່' },
            'top-right', 'danger', 5000
          );
          this.fetchData();
        }
      }).catch(error => {
        this.removeLoading = false;
        this.removeModalActive = false;
        if (error.response && error.response.status === 422) {
          this.$notification.openNotification(
            { title: 'DC Code', text: this.serverErrorMessage(error.response.data?.error) },
            'top-right', 'danger', 5000
          );
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    serverErrorMessage(errorCode) {
      if (errorCode === 'TABLE_MISSING') {
        return 'ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ)';
      }
      if (errorCode === 'BRANCH_NOT_FOUND') {
        return 'ບໍ່ພົບສາຂານີ້ໃນລະບົບ';
      }
      return 'ບໍ່ສາມາດບັນທຶກໄດ້ ກະລຸນາກວດ prefix ທີ່ເລືອກຄືນ';
    },
  },
};
</script>
<style lang="scss" scoped>
.dc-code-panel {
  margin-top: 10px;

  .dc-code-hint {
    color: #666;
    font-size: 14px;
    margin-top: 6px;
  }

  .dc-code-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px;

    &.is-overridden {
      border-color: #195BFF;
    }

    .dc-code-value {
      font-size: 26px;
      font-weight: 600;
      margin-top: 4px;
    }

    .dc-code-note {
      margin-top: 6px;
      font-size: 13px;
      color: #195BFF;

      &.is-muted {
        color: #999;
      }
    }
  }

  .dc-code-actions {
    display: flex;
    align-items: flex-end;

    .vs-button {
      margin-right: 8px;
    }
  }
}
</style>
